// Dynamic metadata form: renders a document type's attributes grouped by section.
import React from 'react';
import type { Attribute, Assignee, DocType } from '../types';
import { Combobox, DatePicker, Field, type ComboOption } from '../ui';
import { useCatalog } from '../catalog';
import { Icon } from '../ui/Icon';
import { api } from '../api';
import { useToast } from '../ui';
import { OcrBadge } from './Ocr';
import type { Suggestion } from '../types';
import { sectionDirection } from './Response';
import { LocationPicker, isLocation, locationText } from '../maps/components';

export function groupBySection(attrs: Attribute[]): { section: string; attrs: Attribute[] }[] {
  const out: { section: string; attrs: Attribute[] }[] = [];
  for (const a of attrs) {
    const s = a.section || '';
    let g = out.find(x => x.section === s);
    if (!g) { g = { section: s, attrs: [] }; out.push(g); }
    g.attrs.push(a);
  }
  return out;
}

/**
 * The one layout used everywhere a document's attributes appear (registration form, edit form, document view, list columns
 * and their filters), so they always show the same fields, with the same names, in the same order:
 *   registration date (next to the registration number) → 1 Intrare → 2 Distribuire (ASSIGNEE) → 3 Răspuns → 4 Ieșire → other sections.
 */
export interface DocLayout { hasDirections: boolean; regDate: Attribute | null; main: Attribute[]; mainSection: string; assign: Attribute[]; out: Attribute[]; outSection: string; gen: { section: string; attrs: Attribute[] }[] }
export function docLayout(all: Attribute[], isRegistry: boolean, direction: 'IN' | 'OUT' = 'IN'): DocLayout {
  const dir = (a: Attribute) => sectionDirection(a.section);
  const hasDirections = all.some(a => dir(a) === 'IN') && all.some(a => dir(a) === 'OUT');
  const regDate = isRegistry ? (all.find(a => a.type === 'DATE' && a.options?.defaultToday && (!hasDirections || dir(a) === direction)) || null) : null;
  const notReg = (a: Attribute) => !regDate || a.code !== regDate.code;
  if (!hasDirections) return { hasDirections, regDate, main: [], mainSection: '', assign: [], out: [], outSection: '', gen: groupBySection(all.filter(notReg)) };
  const main = all.filter(a => dir(a) === direction && a.type !== 'ASSIGNEE' && notReg(a));
  const assign = all.filter(a => a.type === 'ASSIGNEE');
  // a received document also carries the "Ieșire" fields, filled in when the registry sends the answer
  const out = direction === 'IN' ? all.filter(a => dir(a) === 'OUT' && a.type !== 'ASSIGNEE') : [];
  const sec = (d: 'IN' | 'OUT') => all.find(a => dir(a) === d)?.section || '';
  return { hasDirections, regDate, main, mainSection: sec(direction), assign, out, outSection: sec('OUT'), gen: groupBySection(all.filter(a => dir(a) == null && a.type !== 'ASSIGNEE' && notReg(a))) };
}
/** Column order of the document lists: the same order as the form and the view. */
export function attrOrder(all: Attribute[]): Attribute[] {
  const dir = (a: Attribute) => sectionDirection(a.section);
  if (!(all.some(a => dir(a) === 'IN') && all.some(a => dir(a) === 'OUT'))) return all;
  return [...all.filter(a => dir(a) === 'IN' && a.type !== 'ASSIGNEE'), ...all.filter(a => a.type === 'ASSIGNEE'), ...all.filter(a => dir(a) === 'OUT' && a.type !== 'ASSIGNEE'), ...all.filter(a => dir(a) == null && a.type !== 'ASSIGNEE')];
}
/** "Intrare (documente primite)" → "documente primite" */
export const sectionSub = (section: string, fallback: string) => section.replace(/^[^(]*\(?/, '').replace(/\)$/, '').trim() || fallback;

interface Props { type: DocType; value: Record<string, any>; onChange: (v: Record<string, any>) => void; errors?: Record<string, string>; disabled?: boolean; onListChanged?: () => void; locked?: Record<string, string>; noTitles?: boolean; marks?: Record<string, Suggestion> }

export function MetaFields({ type, value, onChange, errors, disabled, onListChanged, locked, noTitles, marks }: Props) {
  const groups = groupBySection(type.attributes || []);
  const set = (code: string, v: any) => onChange({ ...value, [code]: v });
  return (
    <>
      {groups.map(g => (
        <div key={g.section}>
          {g.section && !noTitles && <div className="section-title">{g.section}</div>}
          <div className="form-grid">
            {g.attrs.map(a => (
              <Field key={a.code} label={marks?.[a.code] ? <>{a.name}<OcrBadge s={marks[a.code]} /></> : a.name} required={!!a.required} hint={locked?.[a.code] || a.description || undefined} error={errors?.[a.code]} className={(a.type === 'TEXTAREA' || a.type === 'LOCATION' ? 'span2 ' : '') + (locked?.[a.code] ? 'locked-field' : '') + (marks?.[a.code] ? (marks[a.code].confidence === 'CHECK' ? ' ocr-check' : ' ocr-filled') : '')}>
                <AttrInput attr={a} value={value[a.code]} onChange={v => set(a.code, v)} disabled={disabled || !!locked?.[a.code]} invalid={!!errors?.[a.code]} onListChanged={onListChanged} />
              </Field>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function AttrInput({ attr, value, onChange, disabled, invalid, onListChanged }: { attr: Attribute; value: any; onChange: (v: any) => void; disabled?: boolean; invalid?: boolean; onListChanged?: () => void }) {
  const { departments, users } = useCatalog();
  const toast = useToast();
  switch (attr.type) {
    case 'TEXTAREA': return <textarea className={'textarea ' + (invalid ? 'invalid' : '')} value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled} />;
    case 'NUMBER': return <input className={'input ' + (invalid ? 'invalid' : '')} type="number" step="any" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : e.target.value)} disabled={disabled} />;
    case 'DATE': return <DatePicker value={value ?? null} onChange={onChange} disabled={disabled} invalid={invalid} />;
    case 'DATETIME': return <input className={'input ' + (invalid ? 'invalid' : '')} type="datetime-local" value={value ?? ''} onChange={e => onChange(e.target.value || null)} disabled={disabled} />;
    case 'BOOLEAN': return <label className="checkbox" style={{ padding: '7px 0' }}><input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} disabled={disabled} /><span>{value ? 'Da' : 'Nu'}</span></label>;
    case 'LIST': {
      const opts: ComboOption[] = (attr.items || []).map(i => ({ value: i.value, label: i.label || i.value, color: i.color }));
      return <Combobox value={value ?? null} options={opts} onChange={v => onChange(v)} allowAdd={!!attr.listAllowAdd} disabled={disabled} invalid={invalid}
        onAdd={async text => {
          try {
            const it = await api.post<{ value: string; label: string }>(`/api/catalog/lists/${attr.listId}/items`, { value: text });
            toast(`„${it.label}” a fost adăugat în lista ${attr.listName || ''}`, 'ok');
            onListChanged?.();
            return { value: it.value, label: it.label };
          } catch (e: any) { toast(e.message, 'error'); return { value: text, label: text }; }
        }} />;
    }
    case 'DEPARTMENT': {
      const v = value && typeof value === 'object' ? (value as Assignee).departmentId : value;
      return <Combobox value={v ?? null} options={departments.filter(d => d.active).map(d => ({ value: d.id, label: d.name }))} onChange={(id, o) => onChange(id == null ? null : { departmentId: Number(id), departmentName: o?.label })} disabled={disabled} invalid={invalid} />;
    }
    case 'USER': {
      const v = value && typeof value === 'object' ? (value as Assignee).userId : value;
      return <Combobox value={v ?? null} options={users.map(u => ({ value: u.id, label: u.displayName, sub: u.username }))} onChange={(id, o) => onChange(id == null ? null : { userId: Number(id), userName: o?.label })} disabled={disabled} invalid={invalid} />;
    }
    case 'ASSIGNEE': return <MultiAssignee value={value} onChange={onChange} disabled={disabled} invalid={invalid} />;
    case 'LOCATION': return <LocationPicker value={isLocation(value) ? value : null} onChange={onChange} disabled={disabled} required={!!attr.required} height={260} />;
    default: return <input className={'input ' + (invalid ? 'invalid' : '')} value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled} />;
  }
}

/** Human readable rendering of a metadata value. */
export function renderValue(attr: Attribute | undefined, v: any): React.ReactNode {
  if (v == null || v === '') return <span className="muted">—</span>;
  if (attr?.type === 'DATE') { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v)); return m ? `${m[3]}.${m[2]}.${m[1]}` : String(v); }
  if (attr?.type === 'BOOLEAN') return v ? 'Da' : 'Nu';
  if (isLocation(v)) return locationText(v);
  if (Array.isArray(v)) return v.length ? v.map((a: Assignee) => [a.departmentName, a.userName].filter(Boolean).join(' / ')).join(', ') : <span className="muted">—</span>;
  if (typeof v === 'object') { const a = v as Assignee; return [a.departmentName, a.userName].filter(Boolean).join(' / ') || JSON.stringify(v); }
  if (attr?.type === 'LIST') { const it = attr.items?.find(i => i.value === v); return it ? (it.color ? <span className="row" style={{ gap: 6 }}><span className="color-dot" style={{ background: it.color }} />{it.label}</span> : it.label) : String(v); }
  return String(v);
}

/** Several departments (each with an optional person). Value: [{departmentId, departmentName, userId?, userName?}] */
export function toAssignees(value: any): Assignee[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === 'object' && (value.departmentId || value.userId)) return [value];
  return [];
}
function MultiAssignee({ value, onChange, disabled, invalid }: { value: any; onChange: (v: any) => void; disabled?: boolean; invalid?: boolean }) {
  const { departments, users } = useCatalog();
  const list = toAssignees(value);
  const set = (next: Assignee[]) => onChange(next.length ? next : null);
  const used = new Set(list.map(a => a.departmentId));
  const depOpts = departments.filter(d => d.active && !used.has(d.id)).map(d => ({ value: d.id, label: d.name, sub: `${users.filter(u => u.departments.includes(d.id)).length} pers.` }));
  return (
    <div className="stack" style={{ gap: 6 }}>
      {list.map((a, i) => {
        const depUsers = a.departmentId ? users.filter(u => u.departments.includes(a.departmentId!)) : users;
        return (
          <div key={(a.departmentId ?? 'u') + '-' + i} className="assignee-row">
            <span className="assignee-dep"><Icon name="users" size={14} />{a.departmentName || '—'}</span>
            <Combobox value={a.userId ?? null} placeholder="Persoană (opțional)…" options={depUsers.map(u => ({ value: u.id, label: u.displayName, sub: u.username }))} disabled={disabled}
              onChange={(id, o) => set(list.map((x, j) => j === i ? (id == null ? { departmentId: x.departmentId, departmentName: x.departmentName } : { ...x, userId: Number(id), userName: o?.label }) : x))} />
            {!disabled && <button type="button" className="icon-btn" title="Scoate compartimentul" onClick={() => set(list.filter((_, j) => j !== i))}><Icon name="x" size={15} /></button>}
          </div>
        );
      })}
      {!disabled && (
        <Combobox value={null} placeholder={list.length ? '+ Adaugă încă un compartiment…' : 'Alegeți compartimentele…'} options={depOpts} invalid={invalid} clearable={false}
          onChange={(id, o) => { if (id != null) set([...list, { departmentId: Number(id), departmentName: o?.label }]); }} />
      )}
    </div>
  );
}
